import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import toast from 'react-hot-toast'

// ═══════════════════════════════════════════════════════════════
// 📲 NSV SIGNING APP — Mobile-first assignment sequence
// Route: /notary/assignment/:jobId
// Nancy's vision: Guide notaries step by step through every job
// ═══════════════════════════════════════════════════════════════

const APP_STYLES = `
  * { box-sizing: border-box; }
  body { background: #E9EAEC; }

  .sa-wrap { max-width: 480px; margin: 0 auto; padding: 16px; min-height: 100vh; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }

  /* HEADER */
  .sa-header { background: linear-gradient(135deg,#4D4E8D,#41427a); border-radius: 16px; padding: 20px; margin-bottom: 16px; position: relative; overflow: hidden; }
  .sa-header::before { content: ''; position: absolute; top: -40px; right: -40px; width: 120px; height: 120px; background: radial-gradient(circle, rgba(77,78,141,0.15), transparent 70%); }
  .sa-header-top { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; }
  .sa-back-btn { background: rgba(255,255,255,0.1); border: none; border-radius: 10px; width: 36px; height: 36px; display: flex; align-items: center; justify-content: center; cursor: pointer; color: #fff; font-size: 18px; flex-shrink: 0; }
  .sa-job-number { font-size: 11px; color: rgba(255,255,255,0.5); letter-spacing: 0.1em; margin-bottom: 2px; }
  .sa-job-type { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 18px; color: #fff; font-weight: 600; }
  .sa-job-meta { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
  .sa-meta-item { background: rgba(255,255,255,0.08); border-radius: 10px; padding: 10px 12px; }
  .sa-meta-label { font-size: 9px; color: rgba(255,255,255,0.4); letter-spacing: 0.1em; text-transform: uppercase; margin-bottom: 3px; }
  .sa-meta-value { font-size: 13px; color: #fff; font-weight: 500; }
  .sa-meta-value.gold { color: #fff; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 18px; }

  /* PROGRESS */
  .sa-progress-wrap { background: #fff; border-radius: 14px; padding: 16px; margin-bottom: 16px; border: 1px solid #E2E4E7; }
  .sa-progress-label { font-size: 11px; font-weight: 700; color: #5C6065; letter-spacing: 0.08em; margin-bottom: 10px; }
  .sa-progress-bar { background: #E2E4E7; border-radius: 99px; height: 8px; overflow: hidden; margin-bottom: 8px; }
  .sa-progress-fill { height: 100%; border-radius: 99px; background: linear-gradient(90deg, #4D4E8D, #6E70AE); transition: width 0.5s ease; }
  .sa-progress-text { font-size: 12px; color: #4D4E8D; font-weight: 700; text-align: right; }

  /* STEPS */
  .sa-steps { display: flex; flex-direction: column; gap: 10px; margin-bottom: 16px; }
  .sa-step { border-radius: 14px; overflow: hidden; border: 2px solid transparent; transition: all 0.2s; }
  .sa-step.completed { border-color: rgba(45,122,79,0.3); background: #fff; }
  .sa-step.active { border-color: #4D4E8D; background: #fff; box-shadow: 0 4px 20px rgba(77,78,141,0.15); }
  .sa-step.locked { border-color: #E2E4E7; background: #F5F6F7; opacity: 0.6; }
  .sa-step-header { display: flex; align-items: center; gap: 12px; padding: 14px 16px; }
  .sa-step-dot { width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: 700; flex-shrink: 0; }
  .sa-step.completed .sa-step-dot { background: #5E8A72; color: #fff; }
  .sa-step.active .sa-step-dot { background: linear-gradient(135deg, #4D4E8D, #41427a); color: #fff; }
  .sa-step.locked .sa-step-dot { background: #E2E4E7; color: #A2A6AA; }
  .sa-step-info { flex: 1; }
  .sa-step-title { font-size: 14px; font-weight: 600; color: #151715; }
  .sa-step.locked .sa-step-title { color: #A2A6AA; }
  .sa-step-sub { font-size: 11px; color: #5C6065; margin-top: 2px; }
  .sa-step-ts { font-size: 10px; color: #5E8A72; font-weight: 600; margin-top: 2px; }
  .sa-step-body { padding: 0 16px 16px; }

  /* BUTTONS */
  .sa-btn { width: 100%; padding: 16px; border-radius: 12px; border: none; font-size: 15px; font-weight: 700; cursor: pointer; transition: all 0.2s; font-family: inherit; display: flex; align-items: center; justify-content: center; gap: 10px; }
  .sa-btn:active { transform: scale(0.98); }
  .sa-btn-primary { background: linear-gradient(135deg,#4D4E8D,#41427a); color: #fff; }
  .sa-btn-primary:hover { background: linear-gradient(135deg,#41427a,#4D4E8D); }
  .sa-btn-green { background: linear-gradient(135deg, #5E8A72, #3A9A65); color: #fff; }
  .sa-btn-green:hover { background: linear-gradient(135deg, #3A9A65, #5E8A72); }
  .sa-btn-gold { background: linear-gradient(135deg, #4D4E8D, #41427a); color: #fff; }
  .sa-btn-outline { background: #fff; color: #5C6065; border: 1.5px solid #D7DADE; }
  .sa-btn-danger { background: #FDF2F2; color: #E24B4A; border: 1.5px solid rgba(226,75,74,0.3); }
  .sa-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .sa-btn-sm { padding: 10px 16px; font-size: 13px; border-radius: 10px; }

  /* INPUT */
  .sa-input { width: 100%; padding: 12px 14px; border: 1.5px solid #D7DADE; border-radius: 10px; font-size: 14px; font-family: inherit; transition: border-color 0.15s; background: #fff; }
  .sa-input:focus { outline: none; border-color: #4D4E8D; }
  .sa-label { font-size: 11px; font-weight: 700; color: #5C6065; letter-spacing: 0.08em; margin-bottom: 6px; display: block; }

  /* UPLOAD */
  .sa-upload-zone { border: 2px dashed #D7DADE; border-radius: 12px; padding: 20px; text-align: center; cursor: pointer; transition: all 0.15s; background: #F7F8F9; }
  .sa-upload-zone:hover { border-color: #4D4E8D; background: #F5F6F7; }
  .sa-upload-zone.uploaded { border-color: #5E8A72; background: #EAF2EE; }

  /* TOAST CELEBRATION */
  @keyframes saCelebrate { 0%{transform:scale(0.5);opacity:0} 60%{transform:scale(1.1)} 100%{transform:scale(1);opacity:1} }
  .sa-celebration { background: linear-gradient(135deg,#4D4E8D,#41427a); border-radius: 20px; padding: 32px 24px; text-align: center; margin-bottom: 16px; border: 2px solid #4D4E8D; animation: saCelebrate 0.4s ease; }

  /* ADDRESS */
  .sa-address-card { background: #EAF2EE; border: 1px solid rgba(45,122,79,0.2); border-radius: 12px; padding: 14px; margin-bottom: 12px; }
  .sa-address-label { font-size: 10px; font-weight: 700; color: #5E8A72; letter-spacing: 0.1em; margin-bottom: 4px; }
  .sa-address-text { font-size: 14px; color: #151715; font-weight: 500; }

  /* DECLINE MODAL */
  .sa-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.7); z-index: 1000; display: flex; align-items: flex-end; padding: 0; }
  .sa-modal { background: #fff; border-radius: 20px 20px 0 0; padding: 24px; width: 100%; max-height: 80vh; overflow-y: auto; }
  .sa-modal-title { font-size: 18px; font-weight: 700; color: #151715; margin-bottom: 16px; }
`

// ── STEP DEFINITIONS ──────────────────────────────────────────
const STEPS = [
  { id: 'accept',      icon: '📋', title: 'Accept Assignment',        sub: 'Review and confirm you are taking this job' },
  { id: 'confirm',     icon: '📞', title: 'Confirm with Borrower',    sub: 'Call and confirm appointment details' },
  { id: 'on_way',      icon: '🚗', title: 'On My Way',                sub: 'Tap when you leave for the appointment' },
  { id: 'arrived',     icon: '📍', title: 'Arrived',                  sub: 'Tap when you arrive at the location' },
  { id: 'completed',   icon: '✅', title: 'Signing Complete',         sub: 'Tap when all documents are signed' },
  { id: 'scanbacks',   icon: '📄', title: 'Scanbacks Uploaded',       sub: 'Upload scanned documents via your portal' },
  { id: 'shipping',    icon: '📦', title: 'Ready to Ship',            sub: 'Package is ready — add tracking info' },
  { id: 'tracking',    icon: '🚚', title: 'Tracking Updated',         sub: 'Enter carrier and tracking number' },
  { id: 'receipt',     icon: '🧾', title: 'Receipt Uploaded',         sub: 'Upload your shipping receipt' },
]

const DECLINE_REASONS = [
  'Schedule conflict',
  'Too far to travel',
  'Personal emergency',
  'Not comfortable with document type',
  'Other',
]

// ── ENCOURAGEMENT MESSAGES ────────────────────────────────────
const MESSAGES = {
  accept:    { msg: "You're on it!! Your professionalism starts NOW!! 💛", color: '#4D4E8D' },
  confirm:   { msg: "Great!! The borrower appreciates the heads up!! 📞", color: '#185FA5' },
  on_way:    { msg: "Drive safe!! You've got this!! 🚗", color: '#5E8A72' },
  arrived:   { msg: "You showed up — that's everything!! 📍", color: '#6A3A9A' },
  completed: { msg: "Outstanding work!! The tribe is proud of you!! ✅", color: '#5E8A72' },
  scanbacks: { msg: "QC will love those clean scans!! 📄", color: '#185FA5' },
  shipping:  { msg: "Almost there — package prepped like a pro!! 📦", color: '#4D4E8D' },
  tracking:  { msg: "Tracking submitted!! Client is notified!! 🚚", color: '#5E8A72' },
  receipt:   { msg: "", color: '#4D4E8D' },
}

// ── STATUS → STEP MAP ─────────────────────────────────────────
// Which steps are shown for THIS job. Individual jobs (or jobs flagged
// no-scanbacks) don't do scanbacks, so that step is hidden for them.
function visibleSteps(job) {
  const skip = (job?.job_type === 'individual') || (job?.scan_backs_required === false)
  // Individual / no-scanback jobs end at "Signing Complete" — no scanbacks, and no
  // shipping/tracking/receipt (nothing to ship back; they keep their own documents).
  const hidden = ['scanbacks', 'shipping', 'tracking', 'receipt']
  return STEPS.filter(s => !(skip && hidden.includes(s.id)))
}

function getStepFromStatus(job) {
  const skip  = (job?.job_type === 'individual') || (job?.scan_backs_required === false)
  const steps = visibleSteps(job)
  const s = job?.status

  // Fully finished
  if (['payment_received','notary_paid','closed'].includes(s)) return steps.length
  // Individual / no-scanback jobs finish at "Signing Complete" — nothing to ship back
  if (skip && ['completed','scanbacks_uploaded','client_approved'].includes(s)) return steps.length

  // Post-signing return flow: scanbacks → shipping → tracking → receipt, by what's actually done.
  // 'scanbacks_uploaded' = awaiting QC (shipping step shows the hold). 'client_approved' = QC cleared.
  if (['scanbacks_uploaded','client_approved'].includes(s)) {
    let id
    if (!job?.shipped_at)            id = 'shipping'
    else if (!job?.tracking_number)  id = 'tracking'
    else                             id = 'receipt'
    const i = steps.findIndex(st => st.id === id)
    return i < 0 ? steps.length : i
  }

  let id
  if (s === 'completed')        id = 'scanbacks'
  else if (s === 'arrived')     id = 'completed'
  else if (s === 'on_the_way')  id = 'arrived'
  else if (s === 'accepted')    id = job?.confirmed_at ? 'on_way' : 'confirm'
  else id = 'accept'
  const i = steps.findIndex(st => st.id === id)
  return i < 0 ? 0 : i
}

// ── GPS HELPER ────────────────────────────────────────────────
function getGPS() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) { resolve(null); return }
    navigator.geolocation.getCurrentPosition(
      pos => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      ()  => resolve(null),
      { timeout: 8000 }
    )
  })
}

// ── MAIN COMPONENT ────────────────────────────────────────────
export default function NotaryAssignment() {
  const { jobId }    = useParams()
  const navigate     = useNavigate()
  const { user }     = useAuth()
  const [job,        setJob]        = useState(null)
  const [loading,    setLoading]    = useState(true)
  const [currentStep,setCurrentStep]= useState(0)
  const [timestamps, setTimestamps] = useState({})
  const [saving,     setSaving]     = useState(false)
  const [showDecline,setShowDecline]= useState(false)
  const [declineReason,setDeclineReason] = useState('')
  const [trackingNum,setTrackingNum]= useState('')
  const [carrier,    setCarrier]    = useState('FedEx')
  const [receiptUploaded, setReceiptUploaded] = useState(false)
  const [showCelebration, setShowCelebration] = useState(false)
  const [uploading,  setUploading]  = useState(false)
  const receiptRef   = useRef(null)
  const activeStepRef    = useRef(null)
  const trackingInputRef = useRef(null)
  const scanbackInputRef = useRef(null)

  // ✨ Frictionless: when the active step changes, glide to it and (for tracking) pop the field ready
  useEffect(() => {
    const t = setTimeout(() => {
      activeStepRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      if (steps[currentStep]?.id === 'tracking') trackingInputRef.current?.focus()
    }, 280)
    return () => clearTimeout(t)
  }, [currentStep])

  useEffect(() => {
    if (!jobId) return
    loadJob()
    // 🔴 Real-time — refresh this assignment if it changes elsewhere (admin, client, QC, etc.)
    const ch = supabase.channel(`notary-assignment-${jobId}`)
      .on('postgres_changes', { event:'*', schema:'public', table:'jobs', filter:`id=eq.${jobId}` }, () => loadJob())
      .subscribe()
    return () => supabase.removeChannel(ch)
  }, [jobId])

  async function loadJob() {
    const { data } = await supabase.from('jobs').select('*').eq('id', jobId).single()
    if (!data) { toast.error('Job not found!!'); navigate('/notary/signings'); return }
    setJob(data)
    setCurrentStep(getStepFromStatus(data))
    // Restore timestamps from job fields
    const ts = {}
    if (data.accepted_at)    ts.accept    = data.accepted_at
    if (data.confirmed_at)   ts.confirm   = data.confirmed_at
    if (data.on_way_at)      ts.on_way    = data.on_way_at
    if (data.arrived_at)     ts.arrived   = data.arrived_at
    if (data.completed_at)   ts.completed = data.completed_at
    if (data.scanbacks_at)   ts.scanbacks = data.scanbacks_at
    if (data.tracking_number)ts.tracking  = data.shipped_at
    setTimestamps(ts)
    if (data.tracking_number) setTrackingNum(data.tracking_number)
    if (data.shipping_carrier) setCarrier(data.shipping_carrier)
    setLoading(false)
  }

  async function advanceStep(stepId, extraData = {}) {
    setSaving(true)
    const now = new Date().toISOString()
    const gps = ['on_way','arrived'].includes(stepId) ? await getGPS() : null

    const updates = { ...extraData }

    // Map step → status + timestamp field
    const stepMap = {
      accept:    { status: 'accepted',           ts_field: 'accepted_at' },
      confirm:   { status: 'accepted',           event_status: 'confirmed', ts_field: 'confirmed_at' },
      on_way:    { status: 'on_the_way',         ts_field: 'on_way_at' },
      arrived:   { status: 'arrived',            ts_field: 'arrived_at' },
      completed: { status: 'completed',          ts_field: 'completed_at' },
      scanbacks: { status: 'scanbacks_uploaded', ts_field: 'scanbacks_at' },
      shipping:  { status: null,                 ts_field: 'shipped_at' },
      tracking:  { status: null,                 ts_field: null },
      receipt:   { status: null,                 ts_field: null },
    }

    const cfg = stepMap[stepId]
    if (cfg) {
      if (cfg.status) updates.status = cfg.status
      if (cfg.ts_field) updates[cfg.ts_field] = now
    }

    if (gps) { updates.last_lat = gps.lat; updates.last_lng = gps.lng }

    try {
      const { error } = await supabase.from('jobs').update(updates).eq('id', jobId)
      if (error) throw error

      // 🗂️ Log the transition to the timeline so the admin status timeline tracks progress
      if (cfg?.status) {
        try {
          await supabase.from('job_status_events').insert({
            job_id: jobId,
            status: cfg.event_status || cfg.status,
            created_by_role: 'notary',
            created_by: job?.notary_id || null,
          })
        } catch (e) { /* timeline log is best-effort — never block the step */ }
      }

      setTimestamps(prev => ({ ...prev, [stepId]: now }))
      const nextStep = visibleSteps(job).findIndex(s => s.id === stepId) + 1
      setCurrentStep(nextStep)

      const m = MESSAGES[stepId]
      if (m?.msg) toast.success(m.msg, { duration: 4000, style: { background: m.color, color: '#fff', fontWeight: 600 } })

      // Final celebration — fires when the last step for THIS job is done
      // (receipt for full client jobs, "Signing Complete" for individual jobs)
      if (nextStep >= visibleSteps(job).length) {
        setShowCelebration(true)
      }

      loadJob()
    } catch (err) { toast.error(err.message) }
    finally { setSaving(false) }
  }

  async function checkQCStatus() {
    const { data } = await supabase.from('jobs').select('status').eq('id', jobId).single()
    if (data && ['client_approved','closed'].includes(data.status)) {
      toast.success('✅ QC approved — you are cleared to ship!! Enter your tracking below!!', { duration: 4000 })
    } else {
      toast('⏳ Still awaiting QC approval — hang tight, we will clear you the moment it is reviewed!!', { icon: '⏳', duration: 4000 })
    }
    loadJob()
  }

  async function declineJob() {
    if (!declineReason) { toast.error('Please select a reason!!'); return }
    setSaving(true)
    try {
      await supabase.from('jobs').update({
        status:          'awaiting_assignment',
        notary_id:       null,
        decline_reason:  declineReason,
        declined_at:     new Date().toISOString(),
      }).eq('id', jobId)
      toast.success('Assignment declined — NSV has been notified!!')
      navigate('/notary/available')
    } catch (err) { toast.error(err.message) }
    finally { setSaving(false) }
  }

  async function uploadReceipt(file) {
    if (!file) return
    setUploading(true)
    try {
      const path = `receipts/${jobId}-${Date.now()}.${file.name.split('.').pop()}`
      const { error } = await supabase.storage.from('job-documents').upload(path, file, { upsert: true })
      if (error) throw error
      const { data: { publicUrl } } = supabase.storage.from('job-documents').getPublicUrl(path)
      await advanceStep('receipt', { receipt_url: publicUrl })
      setReceiptUploaded(true)
    } catch (err) { toast.error('Upload failed: ' + err.message) }
    finally { setUploading(false) }
  }

  async function uploadScanback(files) {
    if (!files || !files.length) return
    setUploading(true)
    try {
      const { data: { user } } = await supabase.auth.getUser()
      for (const file of Array.from(files)) {
        const path = `scanbacks/${jobId}-${Date.now()}-${file.name}`
        const { error } = await supabase.storage.from('job-documents').upload(path, file, { upsert: true })
        if (error) throw error
        const { data: { publicUrl } } = supabase.storage.from('job-documents').getPublicUrl(path)
        const { error: insErr } = await supabase.from('job_documents').insert({
          job_id: jobId, uploaded_by: user?.id, role: 'notary',
          doc_type: 'scan_back', file_name: file.name,
          file_url: publicUrl, file_size: file.size, qc_status: 'pending',
        })
        if (insErr) throw insErr
      }
      toast.success('✅ Scanbacks uploaded!! Your client + QC can review them now.')
      await advanceStep('scanbacks')
    } catch (err) { toast.error('Upload failed: ' + err.message) }
    finally { setUploading(false) }
  }

  if (loading) return (
    <>
      <style>{APP_STYLES}</style>
      <div className="sa-wrap" style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'100vh' }}>
        <div style={{ textAlign:'center', color:'#A2A6AA' }}>
          <div style={{ fontSize:40, marginBottom:12 }}>📲</div>
          <div style={{ fontSize:14 }}>Loading assignment...</div>
        </div>
      </div>
    </>
  )

  if (!job) return null

  const steps = visibleSteps(job)
  const pct = Math.round((currentStep / steps.length) * 100)
  const address = `${job.signing_address || ''}, ${job.signing_city || ''}, ${job.signing_state || ''} ${job.signing_zip || ''}`.trim()
  const googleUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`
  const appleUrl  = `https://maps.apple.com/?daddr=${encodeURIComponent(address)}`
  const isIOS     = /iPad|iPhone|iPod/.test(navigator.userAgent)

  return (
    <>
      <style>{APP_STYLES}</style>

      {/* DECLINE MODAL */}
      {showDecline && (
        <div className="sa-modal-overlay" onClick={() => setShowDecline(false)}>
          <div className="sa-modal" onClick={e => e.stopPropagation()}>
            <div className="sa-modal-title">Decline Assignment</div>
            <div style={{ fontSize:13, color:'#5C6065', marginBottom:16 }}>Please select a reason — NSV will be notified immediately!!</div>
            <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:20 }}>
              {DECLINE_REASONS.map(r => (
                <button key={r} type="button" onClick={() => setDeclineReason(r)}
                  style={{ padding:'12px 16px', borderRadius:10, border:`2px solid ${declineReason===r?'#E24B4A':'#E2E4E7'}`, background: declineReason===r?'#FDF2F2':'#fff', color: declineReason===r?'#E24B4A':'#3A3D40', fontWeight: declineReason===r?700:400, cursor:'pointer', textAlign:'left', fontSize:14, fontFamily:'inherit' }}>
                  {r}
                </button>
              ))}
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <button className="sa-btn sa-btn-outline" onClick={() => setShowDecline(false)}>Cancel</button>
              <button className="sa-btn sa-btn-danger" disabled={!declineReason || saving} onClick={declineJob}>
                {saving ? 'Declining...' : 'Decline Assignment'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="sa-wrap">

        {/* CELEBRATION */}
        {showCelebration && (
          <div className="sa-celebration">
            <div style={{ fontSize:48, marginBottom:8 }}>🎉</div>
            <div style={{ fontFamily:'Georgia,serif', fontSize:24, color:'#4D4E8D', marginBottom:8 }}>Assignment Complete!!</div>
            <div style={{ fontSize:14, color:'rgba(255,255,255,0.8)', lineHeight:1.7, marginBottom:20 }}>
              Amazing work!! Keep up the excellence —<br />your tribe is proud of you!! 💛
            </div>
            <button className="sa-btn sa-btn-gold" onClick={() => navigate('/notary/signings')}>
              View My Signings
            </button>
          </div>
        )}

        {/* HEADER */}
        {!showCelebration && (
          <>
            <div className="sa-header">
              <div className="sa-header-top">
                <button className="sa-back-btn" onClick={() => navigate('/notary/signings')}>←</button>
                <div>
                  <div className="sa-job-number">JOB #{job.job_number || job.id?.slice(0,8).toUpperCase()}</div>
                  <div className="sa-job-type">{job.signing_type || job.service_type || 'Loan Signing'}</div>
                </div>
              </div>
              <div className="sa-job-meta">
                <div className="sa-meta-item">
                  <div className="sa-meta-label">📅 Date</div>
                  <div className="sa-meta-value">{job.scheduled_at ? new Date(job.scheduled_at).toLocaleDateString('en-US',{month:'short',day:'numeric'}) : 'TBD'}</div>
                </div>
                <div className="sa-meta-item">
                  <div className="sa-meta-label">🕐 Time</div>
                  <div className="sa-meta-value">{job.scheduled_at ? new Date(job.scheduled_at).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit'}) : 'TBD'}</div>
                </div>
                <div className="sa-meta-item">
                  <div className="sa-meta-label">💰 Fee</div>
                  <div className="sa-meta-value gold">${job.notary_pay || job.notary_fee || job.fee || '—'}</div>
                </div>
                <div className="sa-meta-item">
                  <div className="sa-meta-label">👤 Signer{Array.isArray(job.additional_signers) && job.additional_signers.length > 0 ? 's' : ''}</div>
                  <div className="sa-meta-value">{job.signer_name || job.borrower_name || 'Pending'}{Array.isArray(job.additional_signers) && job.additional_signers.length > 0 ? `, ${job.additional_signers.map(s => s.name).filter(Boolean).join(', ')}` : ''}</div>
                </div>
              </div>
            </div>

            {/* ADDRESS */}
            {address.length > 5 && (
              <div className="sa-address-card">
                <div className="sa-address-label">📍 SIGNING LOCATION</div>
                <div className="sa-address-text">{address}</div>
                <a href={isIOS ? appleUrl : googleUrl} target="_blank" rel="noreferrer"
                  style={{ display:'inline-flex', alignItems:'center', gap:6, marginTop:8, fontSize:12, color:'#5E8A72', fontWeight:600, textDecoration:'none' }}>
                  🗺️ Open in Maps →
                </a>
              </div>
            )}

            {/* PROGRESS */}
            <div className="sa-progress-wrap">
              <div className="sa-progress-label">ASSIGNMENT PROGRESS</div>
              <div className="sa-progress-bar"><div className="sa-progress-fill" style={{ width:`${pct}%` }} /></div>
              <div className="sa-progress-text">{currentStep}/{steps.length} steps · {pct}%</div>
            </div>

            {/* STEPS */}
            <div className="sa-steps">
              {steps.map((step, idx) => {
                const isDone    = idx < currentStep
                const isActive  = idx === currentStep
                const isLocked  = idx > currentStep
                const ts        = timestamps[step.id]

                return (
                  <div key={step.id} ref={isActive ? activeStepRef : null} className={`sa-step ${isDone?'completed':isActive?'active':'locked'}`}>
                    <div className="sa-step-header">
                      <div className="sa-step-dot">
                        {isDone ? '✓' : step.icon}
                      </div>
                      <div className="sa-step-info">
                        <div className="sa-step-title">{step.title}</div>
                        <div className="sa-step-sub">{step.sub}</div>
                        {isDone && ts && (
                          <div className="sa-step-ts">✓ {new Date(ts).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit'})} · {new Date(ts).toLocaleDateString('en-US',{month:'short',day:'numeric'})}</div>
                        )}
                      </div>
                      {isDone && <span style={{ color:'#5E8A72', fontSize:18 }}>✓</span>}
                    </div>

                    {/* ACTIVE STEP CONTENT */}
                    {isActive && (
                      <div className="sa-step-body">

                        {/* ACCEPT */}
                        {step.id === 'accept' && (
                          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                            <div style={{ fontSize:13, color:'#5C6065', lineHeight:1.6, marginBottom:4 }}>
                              Review the job details above and confirm you are accepting this assignment!!
                            </div>
                            <button className="sa-btn sa-btn-green" disabled={saving} onClick={() => advanceStep('accept')}>
                              {saving ? '...' : '✅ Accept Assignment'}
                            </button>
                            <button className="sa-btn sa-btn-danger" onClick={() => setShowDecline(true)}>
                              ✕ Decline Assignment
                            </button>
                          </div>
                        )}

                        {/* CONFIRM WITH BORROWER */}
                        {step.id === 'confirm' && (
                          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                            {job.signer_phone && (
                              <a href={`tel:${job.signer_phone}`} className="sa-btn sa-btn-outline" style={{ textDecoration:'none', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                                📞 Call {job.signer_name || 'Borrower'} — {job.signer_phone}
                              </a>
                            )}
                            <div style={{ fontSize:12, color:'#5C6065', lineHeight:1.6 }}>
                              Confirm the date, time, and address with the borrower. Let them know your name and that you are from NSV — Nationwide Notary Professionals Network!!
                            </div>
                            <button className="sa-btn sa-btn-green" disabled={saving} onClick={() => advanceStep('confirm')}>
                              {saving ? '...' : '✅ Appointment Confirmed'}
                            </button>
                          </div>
                        )}

                        {/* ON MY WAY */}
                        {step.id === 'on_way' && (
                          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                            <div style={{ fontSize:12, color:'#5C6065', lineHeight:1.6 }}>
                              Tap when you leave for the appointment!! Your GPS location will be logged automatically!!
                            </div>
                            <button className="sa-btn sa-btn-primary" disabled={saving} onClick={() => advanceStep('on_way')}>
                              {saving ? 'Logging...' : '🚗 I\'m On My Way!!'}
                            </button>
                          </div>
                        )}

                        {/* ARRIVED */}
                        {step.id === 'arrived' && (
                          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                            <div style={{ fontSize:12, color:'#5C6065', lineHeight:1.6 }}>
                              You have arrived!! Your arrival time will be logged!! Remember — introduce yourself professionally by name!!
                            </div>
                            <button className="sa-btn sa-btn-primary" disabled={saving} onClick={() => advanceStep('arrived')}>
                              {saving ? 'Logging...' : '📍 I Have Arrived!!'}
                            </button>
                          </div>
                        )}

                        {/* SIGNING COMPLETE */}
                        {step.id === 'completed' && (
                          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                            <div style={{ fontSize:12, color:'#5C6065', lineHeight:1.6 }}>
                              All documents signed and reviewed!! Remember to check for missed signatures and initials before leaving!!
                            </div>
                            <button className="sa-btn sa-btn-green" disabled={saving} onClick={() => advanceStep('completed')}>
                              {saving ? '...' : '✅ Signing Complete — Leaving Now!!'}
                            </button>
                          </div>
                        )}

                        {/* SCANBACKS */}
                        {step.id === 'scanbacks' && (
                          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                            <div style={{ fontSize:12, color:'#5C6065', lineHeight:1.6 }}>
                              Upload your scanned signed documents right here — your client and our QC team can review them immediately. Do NOT email documents unless specifically instructed!!
                            </div>
                            <input ref={scanbackInputRef} type="file" accept="image/*,.pdf" multiple style={{ display:'none' }} onChange={e => uploadScanback(e.target.files)} />
                            <button className="sa-btn sa-btn-primary" disabled={uploading} onClick={() => scanbackInputRef.current?.click()}>
                              {uploading ? '⏳ Uploading...' : '📄 Upload Scanbacks'}
                            </button>
                            <button onClick={() => advanceStep('scanbacks')} disabled={saving} style={{ background:'none', border:'none', color:'#41427a', fontSize:11, textDecoration:'underline', cursor:'pointer', padding:0 }}>
                              Already uploaded in my portal? Mark this step done
                            </button>
                          </div>
                        )}

                        {/* READY TO SHIP — gated until QC approval per NSV policy */}
                        {step.id === 'shipping' && (
                          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                            {!['client_approved','closed'].includes(job?.status) ? (
                              <>
                                <div style={{ background:'#EEF0F8', border:'1px solid #4D4E8D', borderRadius:10, padding:'12px 14px', fontSize:12, color:'#7C6A2E', lineHeight:1.6 }}>
                                  ⏳ <strong>Hold for QC approval!!</strong> Per NSV policy, please do NOT ship the originals until your scanbacks have been reviewed and approved by our QC team. You'll be cleared to ship the moment QC approves — hang tight!! 💛
                                </div>
                                <button className="sa-btn sa-btn-primary" onClick={checkQCStatus}>
                                  🔄 Check QC Status
                                </button>
                              </>
                            ) : (
                              <>
                                <div style={{ fontSize:12, color:'#5C6065', lineHeight:1.6 }}>
                                  ✅ QC approved — you're cleared to ship!! Use binder clips — no staples!! Ship using the carrier and method in your assignment instructions, then enter your tracking right here!!
                                </div>
                                <label className="sa-label">CARRIER</label>
                                <div style={{ display:'flex', gap:8 }}>
                                  {['FedEx','UPS','USPS'].map(c => (
                                    <button key={c} type="button" onClick={() => setCarrier(c)}
                                      style={{ flex:1, padding:'10px 8px', borderRadius:10, border:`2px solid ${carrier===c?'#4D4E8D':'#E2E4E7'}`, background:carrier===c?'#F5F6F7':'#fff', fontWeight:carrier===c?700:400, cursor:'pointer', fontSize:13, fontFamily:'inherit' }}>
                                      {c}
                                    </button>
                                  ))}
                                </div>
                                <label className="sa-label">TRACKING NUMBER</label>
                                <input ref={trackingInputRef} className="sa-input" placeholder="Enter tracking number..." value={trackingNum} onChange={e => setTrackingNum(e.target.value)} />
                                <button className="sa-btn sa-btn-gold" disabled={saving || !trackingNum.trim()} onClick={() => advanceStep('shipping', { tracking_number: trackingNum.trim(), shipping_carrier: carrier })}>
                                  {saving ? '...' : '📦 Confirm Shipped & Submit Tracking'}
                                </button>
                              </>
                            )}
                          </div>
                        )}

                        {/* TRACKING */}
                        {step.id === 'tracking' && (
                          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                            <label className="sa-label">CARRIER</label>
                            <div style={{ display:'flex', gap:8 }}>
                              {['FedEx','UPS','USPS'].map(c => (
                                <button key={c} type="button" onClick={() => setCarrier(c)}
                                  style={{ flex:1, padding:'10px 8px', borderRadius:10, border:`2px solid ${carrier===c?'#4D4E8D':'#E2E4E7'}`, background:carrier===c?'#F5F6F7':'#fff', fontWeight:carrier===c?700:400, cursor:'pointer', fontSize:13, fontFamily:'inherit' }}>
                                  {c}
                                </button>
                              ))}
                            </div>
                            <label className="sa-label">TRACKING NUMBER</label>
                            <input ref={trackingInputRef} className="sa-input" placeholder="Enter tracking number..." value={trackingNum} onChange={e => setTrackingNum(e.target.value)} />
                            <button className="sa-btn sa-btn-green" disabled={saving || !trackingNum.trim()} onClick={() => advanceStep('tracking', { tracking_number: trackingNum.trim(), shipping_carrier: carrier })}>
                              {saving ? 'Saving...' : '🚚 Submit Tracking Number'}
                            </button>
                          </div>
                        )}

                        {/* RECEIPT */}
                        {step.id === 'receipt' && (
                          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                            <div style={{ fontSize:12, color:'#5C6065', lineHeight:1.6 }}>
                              Upload a photo of your shipping receipt to complete this assignment!!
                            </div>
                            <input ref={receiptRef} type="file" accept="image/*,.pdf" style={{ display:'none' }} onChange={e => uploadReceipt(e.target.files[0])} />
                            <div className={`sa-upload-zone${receiptUploaded?' uploaded':''}`} onClick={() => receiptRef.current?.click()}>
                              {uploading ? (
                                <div style={{ color:'#4D4E8D', fontWeight:600 }}>⏳ Uploading receipt...</div>
                              ) : receiptUploaded ? (
                                <div style={{ color:'#5E8A72', fontWeight:600 }}>✅ Receipt uploaded!!</div>
                              ) : (
                                <>
                                  <div style={{ fontSize:32, marginBottom:8 }}>🧾</div>
                                  <div style={{ fontSize:13, fontWeight:600, color:'#151715' }}>Tap to upload receipt</div>
                                  <div style={{ fontSize:11, color:'#A2A6AA', marginTop:4 }}>Photo or PDF</div>
                                </>
                              )}
                            </div>
                          </div>
                        )}

                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </>
        )}
      </div>
    </>
  )
}
